/**
 * O menu, num lugar só.
 *
 * Título, subtítulo, rota e ícone de cada tela vivem aqui — o trilho, o
 * cabeçalho e o roteador leem desta lista. Na versão anterior essas três
 * coisas estavam em três arquivos diferentes, e bastava acrescentar uma
 * tela para elas discordarem: menu com um nome, título com outro.
 */

export type Grupo = 'acompanhar' | 'consultar' | 'personalizar'

export interface Tela {
  rota: string
  nome: string
  titulo: string
  /**
   * CURTO. Diz o que a tela é, em três ou quatro palavras — não como
   * usá-la. O "como usar" mora no botão de ajuda, que tem espaço para
   * passos, recursos e armadilhas; no cabeçalho ele vira uma frase
   * comprida que ninguém lê duas vezes.
   */
  subtitulo: string
  grupo: Grupo
  icone: string
  /**
   * O recurso que precisa estar contratado para esta tela existir.
   *
   * Nem todo cliente tem filial — a maioria é um endereço só. Para
   * esses, "Filiais" no menu é um item que só serve para descobrir que
   * não serve. Quem liga é o painel interno, por contrato; aqui a tela
   * simplesmente não aparece.
   *
   * Esconder no menu NÃO é a proteção: as rotas respondem 403 por
   * conta própria. Isto é sobre não oferecer o que não existe.
   */
  recurso?: string
}

/**
 * NENHUM grupo tem rótulo: o que separa os blocos é uma régua.
 *
 * Havia "ACOMPANHAR", "PERSONALIZAÇÕES" e "SOFTWARE" em versalete.
 * Três problemas de uma vez: o do topo gastava a primeira linha do
 * menu para dizer o óbvio; os outros dois só apareciam com o trilho
 * aberto, então recolhido o menu era uma coluna de ícones sem corte
 * nenhum; e categoria escrita obriga a pessoa a concordar com a
 * classificação antes de achar a tela.
 *
 * A régua faz o trabalho inteiro e não afirma nada. O agrupamento
 * continua existindo — na ORDEM e no espaço —, só não é narrado.
 *
 * O campo `rotulo` fica, vazio: é o que permite voltar a nomear um
 * bloco sem mexer no trilho.
 */
export const GRUPOS: { chave: Grupo; rotulo: string }[] = [
  { chave: 'acompanhar', rotulo: '' },
  { chave: 'consultar', rotulo: '' },
  /* Configurações mora aqui, e não num bloco próprio: régua acima dela
     abria um terceiro corte para um item só, e três réguas num menu de
     onze itens é mais divisa do que conteúdo. Pessoas, Espaços e
     Configurações são a mesma natureza — o que se ajusta de vez em
     quando. */
  { chave: 'personalizar', rotulo: '' },
]

/* Os ícones são o `d` de um <path>, desenhados na mesma grade 16×16 e com
   a mesma espessura de traço. Guardá-los como dado e não como componente
   mantém o trilho legível: ele vira uma lista, não um switch. */
export const TELAS: Tela[] = [
  {
    rota: '/inteligencia', nome: 'Inteligência', titulo: 'Inteligência',
    subtitulo: 'Seu negócio em números e indicadores', grupo: 'acompanhar',
    icone: 'M8 1.6v2M8 12.4v2M1.6 8h2M12.4 8h2M3.5 3.5l1.4 1.4M11.1 11.1l1.4 1.4M12.5 3.5l-1.4 1.4M4.9 11.1l-1.4 1.4',
  },
  {
    rota: '/indicadores', nome: 'Indicadores', titulo: 'Indicadores',
    subtitulo: 'Crie indicadores sob medida para entender sua operação',
    grupo: 'acompanhar', icone: 'M2 13.4V9.2M6 13.4V4.6M10 13.4V7M14 13.4V2.6',
  },
  {
    /* Logo abaixo de Indicadores de propósito: as duas são o par que
       explica o produto — o indicador diz o que aconteceu, a automação
       age no instante em que acontece. Ficava lá embaixo, em
       "Personalizações", ao lado de Espaços e Pessoas, como se fosse
       cadastro, e quem nunca criou uma não descia até lá para
       descobrir que existe. */
    rota: '/automacoes', nome: 'Automações', titulo: 'Automações',
    subtitulo: 'Transforme eventos em alertas instantâneos',
    grupo: 'acompanhar', icone: 'M9 1.5L3.5 9H8l-1 5.5L12.5 7H8z',
  },
  {
    rota: '/eventos', nome: 'Eventos', titulo: 'Eventos',
    subtitulo: 'Visualize registros brutos de eventos capturados pelas câmeras',
    grupo: 'acompanhar', icone: 'M5.5 4h8M5.5 8h8M5.5 12h8',
  },
  {
    /* ═══ JORNADA É A UNIDADE DA MÉTRICA ═══

       Eventos conta AVISTAMENTOS — o que cada câmera viu, e quando. É
       registro, e registro não agrupa: a mesma pessoa vista pela Casa e
       pela Garagem com cinco segundos de diferença são duas linhas ali,
       e têm de ser.

       "Quantas pessoas passaram hoje" não se responde com aquilo, e é a
       pergunta que uma empresa faz. Aqui uma linha é uma PESSOA, do
       momento em que aparece até ir embora, com o caminho que fez.

       ⚠️ E ela TEM começo e fim — é o que a separa do "Rastrear", que
       procura sem limite de tempo e devolve pista. */
    rota: '/jornadas', nome: 'Jornadas', titulo: 'Jornadas',
    subtitulo: 'Uma linha por pessoa: quando chegou, por onde passou, quando saiu',
    grupo: 'acompanhar',
    icone: 'M4 13.5c2-5 4-2 6-6M4 13.5h.01M13 3.5h.01',
  },
  {
    /* Logo depois de Jornadas de propósito: as duas contam o CAMINHO.
       A jornada conta o de uma pessoa, em palavras e por lugar; esta
       conta o de todo mundo somado, desenhado sobre a cena. Quem
       entendeu uma entende a outra na hora. */
    rota: '/circulacao', nome: 'Circulação', titulo: 'Circulação',
    subtitulo: 'Por onde passam e onde ficam parados, na imagem da câmera',
    grupo: 'acompanhar',
    /* Curvas de nível: é literalmente o que um mapa de calor é, e o
       desenho se lê em 16 px sem virar borrão. */
    icone: 'M8 3.4c2.9 0 5 2 5 4.6s-2.1 4.6-5 4.6-5-2-5-4.6 2.1-4.6 5-4.6z'
      + 'M8 6c1.4 0 2.4.9 2.4 2s-1 2-2.4 2-2.4-.9-2.4-2 1-2 2.4-2z',
  },
  {
    rota: '/relatorios', nome: 'Relatórios', titulo: 'Relatórios',
    subtitulo: 'Os números, linha a linha',
    grupo: 'consultar',
    icone: 'M3.4 1.8h6.2l3 3v9.4H3.4zM9.4 1.8v3.2h3.2M5.6 8h5M5.6 10.6h5',
  },
  /* ═══ "BUSCAR" NÃO É ITEM DE MENU, E JÁ FOI DUAS VEZES ═══

     O painel anterior a este não tinha nada chamado Buscar no menu, e a
     razão não é de nome: item de menu é DESTINO, e ninguém abre o
     sistema com a intenção "quero buscar". A intenção é "quero seguir
     aquele carro", e ela nasce olhando a lista.

     ⚠️ Isto dizia "o que existe é a aba RASTREAR, dentro de Eventos".
     A aba saiu em 29/08/2026 e o recurso ficou ainda mais perto da
     lista: virou a janela que o botão "Procurar" abre por cima dela.
     O argumento acima é o mesmo, e uma janela o cumpre melhor que uma
     aba — a lista continua embaixo, com o filtro e a rolagem no lugar.

     A rota /busca continua atendida e redireciona para Eventos:
     endereço antigo, favorito e print com o link não podem virar 404. */
  {
    rota: '/avisos', nome: 'Avisos', titulo: 'Avisos',
    subtitulo: 'O que precisa da sua atenção',
    grupo: 'consultar',
    icone: 'M10.3 2.9L2.6 16.4a1.6 1.6 0 001.4 2.4h12.8a1.6 1.6 0 001.4-2.4'
      + 'L11.7 2.9a1.6 1.6 0 00-2.8 0M10.5 7.6v3.6M10.5 13.6v.1',
  },
  {
    rota: '/cameras', nome: 'Câmeras', titulo: 'Câmeras',
    subtitulo: 'Veja suas câmeras em tempo real', grupo: 'consultar',
    icone: 'M1.8 4.5h8.4v7H1.8zM10.2 8l4 -2.4v4.8z',
  },
  {
    rota: '/pessoas', nome: 'Pessoas', titulo: 'Pessoas',
    subtitulo: 'Quem o sistema chama pelo nome',
    grupo: 'personalizar', icone: 'M2.8 14c0-2.9 2.3-5 5.2-5s5.2 2.1 5.2 5',
  },
  {
    rota: '/espacos', nome: 'Espaços', titulo: 'Espaços',
    subtitulo: 'Os lugares que importam em cada câmera',
    grupo: 'personalizar',
    icone: 'M2.2 5.2l5.8-3 5.8 3v5.6l-5.8 3-5.8-3zM8 8.2l5.8-3M8 8.2v6M8 8.2L2.2 5.2',
  },
  {
    rota: '/filiais', nome: 'Filiais', titulo: 'Filiais',
    subtitulo: 'Os endereços da sua empresa',
    grupo: 'personalizar', recurso: 'filiais',
    icone: 'M2 14V6.4L8 2l6 4.4V14zM6.4 14V9.6h3.2V14',
  },
  {
    rota: '/config', nome: 'Configurações', titulo: 'Configurações',
    subtitulo: 'Câmeras, avisos e armazenamento',
    grupo: 'personalizar',
    icone: 'M8 1.4v1.8M8 12.8v1.8M14.6 8h-1.8M3.2 8H1.4M12.7 3.3l-1.3 1.3M4.6 11.4l-1.3 1.3M12.7 12.7l-1.3-1.3M4.6 4.6L3.3 3.3',
  },
]

export const telaDaRota = (caminho: string): Tela | undefined =>
  TELAS.find(t => caminho === t.rota || caminho.startsWith(t.rota + '/'))
